import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AppError, AUTH_ACTION_TYPE, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { LOGIN_LIMIT_ACTION_TYPE, LOGIN_LIMIT_PROGRESS_ACTION_TYPE, LoginLimitAction } from "./login-limit.action";
import { LOGIN_LIMIT_MESSAGE_CODE, LOGIN_LIMIT_MESSAGE_CONTEXT } from "./login-limit.const";
import { LoginLimitService } from "./login-limit.service";

@Injectable()
export class LoginLimitEffect {
	loginLimit$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_LIMIT_ACTION_TYPE.get),
			switchMap(() =>
				this.service.get().pipe(
					map((response) => this.action.getSuccess(response)),
					catchError((error: AppError) => of(this.action.getFail(error)))
				)
			)
		)
	);

	loginLimitProgress$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_LIMIT_PROGRESS_ACTION_TYPE.get),
			switchMap(() =>
				this.service.getProgress().pipe(
					map((response) => this.action.getProgressSuccess(response)),
					catchError((error: AppError) => of(this.action.getProgressFail(error)))
				)
			)
		)
	);

	loginLimitUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_LIMIT_ACTION_TYPE.update),
			map((x: ActionWithPayload<any>) => x.payload),
			switchMap((params) =>
				this.service.update(params).pipe(
					map(() => this.action.updateSuccess()),
					catchError((error: AppError) => of(this.action.updateFail(error)))
				)
			)
		)
	);

	loginLimitUpdateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_LIMIT_ACTION_TYPE.updateSuccess),
			map(() => this.action.get())
		)
	);

	loginLimitRemove$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_LIMIT_ACTION_TYPE.remove),
			map((x: ActionWithPayload<any>) => x.payload),
			switchMap((params) =>
				this.service.remove(params).pipe(
					map(() => this.action.removeSuccess()),
					catchError((error: AppError) => of(this.action.removeFail(error)))
				)
			)
		)
	);

	loginLimitRemoveSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_LIMIT_ACTION_TYPE.removeSuccess),
			map(() => this.action.get())
		)
	);

	sessionEnd$ = createEffect(() =>
		this.actions$.pipe(
			ofType(AUTH_ACTION_TYPE.sessionEnd),
			map(() => this.action.reset())
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapClearByContext$({
				[LOGIN_LIMIT_ACTION_TYPE.get]: LOGIN_LIMIT_MESSAGE_CONTEXT.get,
				[LOGIN_LIMIT_ACTION_TYPE.update]: LOGIN_LIMIT_MESSAGE_CONTEXT.update,
				[LOGIN_LIMIT_ACTION_TYPE.remove]: LOGIN_LIMIT_MESSAGE_CONTEXT.remove,
			}),
			this.messageService.map$({
				[LOGIN_LIMIT_ACTION_TYPE.getSuccess]: {
					context: LOGIN_LIMIT_MESSAGE_CONTEXT.get,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: "",
				},
				[LOGIN_LIMIT_ACTION_TYPE.getFail]: {
					context: LOGIN_LIMIT_MESSAGE_CONTEXT.get,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: LOGIN_LIMIT_MESSAGE_CODE.getError,
				},
				[LOGIN_LIMIT_ACTION_TYPE.updateSuccess]: {
					context: LOGIN_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: LOGIN_LIMIT_MESSAGE_CODE.updateSuccess,
				},
				[LOGIN_LIMIT_ACTION_TYPE.updateFail]: {
					context: LOGIN_LIMIT_MESSAGE_CONTEXT.update,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: LOGIN_LIMIT_MESSAGE_CODE.updateError,
				},
				[LOGIN_LIMIT_ACTION_TYPE.removeSuccess]: {
					context: LOGIN_LIMIT_MESSAGE_CONTEXT.remove,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: LOGIN_LIMIT_MESSAGE_CODE.removeSuccess,
				},
				[LOGIN_LIMIT_ACTION_TYPE.removeFail]: {
					context: LOGIN_LIMIT_MESSAGE_CONTEXT.remove,
					scope: MessageScopeType.Local,
					type: MessageType.Error,
					messageCode: LOGIN_LIMIT_MESSAGE_CODE.removeError,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: LoginLimitAction,
		private service: LoginLimitService,
		private messageService: MessageService
	) {}
}
